import { useEffect, useState, useRef } from 'react';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { API_BASE_URL, VERSION_CHECK_INTERVAL_MS } from '../config';

export const useVersionCheck = () => {
  const [currentVersion, setCurrentVersion] = useState(() => {
    return localStorage.getItem('appVersion');
  });
  const checkingVersion = useRef(false);

  useEffect(() => {
    const fetchInitialVersion = async () => {
      try {
        if (!localStorage.getItem('userAuthToken')) {
          console.log('[Version] Skipping check - user not authenticated');
          return;
        }

        const lastCheck = localStorage.getItem('appVersionLastCheck');
        const now = Date.now();
        const tenMinutes = VERSION_CHECK_INTERVAL_MS;
        // The throttle only guards the reload-on-new-version logic below; the fetch always
        // runs so the deployed image is visible in the console on every page load.
        const throttled = lastCheck && (now - parseInt(lastCheck)) < tenMinutes;

        const response = await fetchWithAuth(`${API_BASE_URL}/version`);
        const deployedImage = response.headers.get('X-App-Image');
        if (deployedImage) console.info('[Version] Deployed image:', deployedImage);
        const { success, data: version } = await response.json();

        if (!success) return;

        if (throttled) {
          console.log('[Version] Skipping update check - last checked less than 10 minutes ago');
          setCurrentVersion(localStorage.getItem('appVersion'));
          return;
        }

        localStorage.setItem('appVersionLastCheck', now.toString());

        const storedVersion = localStorage.getItem('appVersion');

        if (!storedVersion) {
          localStorage.setItem('appVersion', version);
          setCurrentVersion(version);
        } else if (storedVersion !== version) {
          localStorage.setItem('appVersion', version);
          window.location.reload();
        } else {
          setCurrentVersion(version);
          console.log('[Version] Version is up to date:', version);
        }
      } catch (error) {
        console.error('[Version] Error fetching initial version:', error);
      }
    };

    fetchInitialVersion();
  }, []);

  const checkVersion = async () => {
    if (checkingVersion.current || !currentVersion) return;

    if (!localStorage.getItem('userAuthToken')) {
      console.log('[Version] Skipping check - user not authenticated');
      return;
    }

    const lastCheck = localStorage.getItem('appVersionLastCheck');
    const now = Date.now();
    const tenMinutes = VERSION_CHECK_INTERVAL_MS;

    if (lastCheck && (now - parseInt(lastCheck)) < tenMinutes) {
      console.log('[Version] Skipping check - last checked less than 10 minutes ago');
      return;
    }

    checkingVersion.current = true;

    try {
      console.log('[Version] Checking for updates...');
      const response = await fetchWithAuth(`${API_BASE_URL}/version`);
      const { success, data: version } = await response.json();

      if (!success) return;

      localStorage.setItem('appVersionLastCheck', now.toString());

      if (version !== currentVersion) {
        console.log(`[Version] Version mismatch detected! Current: ${currentVersion}, Server: ${version}`);
        localStorage.setItem('appVersion', version);
        window.location.reload();
      } else {
        console.log('[Version] Version is up to date:', currentVersion);
      }
    } catch (error) {
      console.error('[Version] Error checking version:', error);
    } finally {
      checkingVersion.current = false;
    }
  };

  useEffect(() => {
    if (!currentVersion) return;

    const handleUserReturn = () => {
      console.log('[Version] User returned to the app');
      checkVersion();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleUserReturn();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleUserReturn);

    const tenMinutes = VERSION_CHECK_INTERVAL_MS;
    const intervalId = setInterval(() => {
      console.log('[Version] Periodic check triggered (every 10 min)');
      checkVersion();
    }, tenMinutes);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleUserReturn);
      clearInterval(intervalId);
    };
  }, [currentVersion]);

  return {
    currentVersion,
    checkVersion
  };
};
